import classNames from 'classnames';
import type React from 'react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import * as R from '../../../routes';
import { trackEvent } from '../../services/Tracking';
import { interpolateAppUrl } from '../../services/Url';
import ClickOutside from '../ClickOutside';
import Link from '../Link';
import type { PopoverProps } from '../Popover';
import Popover from '../Popover';
import styles from './styles.m.less';

function getUpgradeLink(): string {
    return interpolateAppUrl(R.SETTINGS_CHECKOUT, {});
}

export type UpgradePlanOnHoverProps = {
    show: boolean;
    children: React.ReactNode;
    placement?: PopoverProps['placement'];
    offset?: PopoverProps['offset'];
    content?: ReactNode;
    containerFullWidth?: boolean;
    hideUpgradeOptionsLink?: boolean;
};

const UpgradePlanOnHover = ({
    children,
    show,
    placement,
    offset,
    content,
    containerFullWidth = false,
    hideUpgradeOptionsLink = false,
}: UpgradePlanOnHoverProps) => {
    const [showPopover, setShowPopover] = useState(false);
    const [mouseInside, setMouseInside] = useState(false);

    useEffect(() => {
        if (!mouseInside && showPopover) {
            const timer = setTimeout(() => {
                setMouseInside(false);
                setShowPopover(false);
            }, 200);
            return () => clearTimeout(timer);
        }
    }, [mouseInside, showPopover]);

    const mouseEntered = () => {
        if (show) {
            setMouseInside(true);
            setShowPopover(true);
        }
    };

    const mouseLeft = () => {
        if (show) {
            setMouseInside(false);
        }
    };

    const handleUpgradeClick = () => {
        trackEvent({
            category: 'upselling',
            action: 'clicked upgrade',
            label: window.location.pathname,
        });
    };

    return (
        <div onMouseEnter={() => mouseEntered()} onMouseLeave={() => mouseLeft()}>
            <ClickOutside
                onClickOutside={() => setShowPopover(false)}
                enabled={showPopover}
                className={classNames(styles.clickOutside, {
                    [styles.clickOutsideFullWidth]: containerFullWidth,
                })}
            >
                <Popover
                    content={
                        <>
                            <p className={styles.DisabledPopoverText}>
                                {content || 'Your current plan doesn’t include this feature.'}
                            </p>
                            {!hideUpgradeOptionsLink && (
                                <Link onClick={() => handleUpgradeClick()} href={getUpgradeLink()}>
                                    See Upgrade Options
                                </Link>
                            )}
                        </>
                    }
                    placement={placement}
                    isShown={showPopover}
                    offset={offset}
                    minWidth={240}
                    containerFullWidth={containerFullWidth}
                >
                    {children}
                </Popover>
            </ClickOutside>
        </div>
    );
};

export default UpgradePlanOnHover;
